import { createFormKeyFromActivityAndQuestion } from '@/helpers/registration-check'
import { Activity } from '@/types/activities'
import { Answer, AnswerResults, Question, QuestionId, QuestionsFormData } from '@/types/questions'
import { exists } from '@lib-helpers'

type FormAnswerValue = QuestionsFormData[string]

export const getSelectedAnswers = (formAnswerValue: FormAnswerValue): string | string[] | null => {
    if (Array.isArray(formAnswerValue)) {
        const selectedAnswers = formAnswerValue.filter((answer) => exists(answer) && answer !== '')
        return selectedAnswers.length > 0 ? selectedAnswers : null
    }

    if (!exists(formAnswerValue) || formAnswerValue === '') {
        return null
    }

    return formAnswerValue
}

const isAnswerSelected = (answer: Answer, selectedAnswers: string | string[]): boolean => {
    if (Array.isArray(selectedAnswers)) {
        return selectedAnswers.includes(answer.id)
    }

    return answer.id === selectedAnswers
}

const getSelectedAnswerTargets = (
    question: Question,
    selectedAnswers: string | string[],
    resultType: AnswerResults,
): QuestionId[] => {
    return question.questionContent.answers
        .filter(
            (answer) =>
                answer.result.type === resultType &&
                answer.result.target &&
                isAnswerSelected(answer, selectedAnswers),
        )
        .map((answer) => answer.result.target as QuestionId)
}

/**
 * Walks reachable questions from the first question based on current answers.
 * - visibleQuestionIds: first question + ShowNextQuestion targets (separate panels)
 * - revealedQuestionIds: all walked questions including ExtendQuestionText targets
 *   (nested in FillInTheBlanks but still requiring an answer)
 */
const getQuestionReachability = (
    questions: Question[],
    activity: Activity,
    formValues: QuestionsFormData,
): { visibleQuestionIds: QuestionId[]; revealedQuestionIds: QuestionId[] } => {
    if (!questions.length) {
        return { visibleQuestionIds: [], revealedQuestionIds: [] }
    }

    const walkQueue: QuestionId[] = [questions[0].id]
    const visibleQuestionIds: QuestionId[] = [questions[0].id]
    const visitedQuestionIds = new Set<QuestionId>([questions[0].id])

    for (let queueIndex = 0; queueIndex < walkQueue.length; queueIndex++) {
        const question = questions.find((candidate) => candidate.id === walkQueue[queueIndex])
        if (!question) {
            continue
        }

        const selectedAnswers = getSelectedAnswers(formValues[createFormKeyFromActivityAndQuestion(activity, question)])
        if (!selectedAnswers) {
            continue
        }

        getSelectedAnswerTargets(question, selectedAnswers, AnswerResults.EXTEND_QUESTION_TEXT).forEach(
            (targetQuestionId) => {
                if (!visitedQuestionIds.has(targetQuestionId)) {
                    visitedQuestionIds.add(targetQuestionId)
                    walkQueue.push(targetQuestionId)
                }
            },
        )

        getSelectedAnswerTargets(question, selectedAnswers, AnswerResults.SHOW_NEXT_QUESTION).forEach(
            (targetQuestionId) => {
                if (!visitedQuestionIds.has(targetQuestionId)) {
                    visitedQuestionIds.add(targetQuestionId)
                    walkQueue.push(targetQuestionId)
                }

                if (!visibleQuestionIds.includes(targetQuestionId)) {
                    visibleQuestionIds.push(targetQuestionId)
                }
            },
        )
    }

    return { visibleQuestionIds, revealedQuestionIds: walkQueue }
}

/**
 * Builds the list of visible question ids by walking reachable question targets from the
 * first question. ExtendQuestionText targets are walked so nested answers can reveal further
 * ShowNextQuestion panels, but only the first question and ShowNextQuestion targets are
 * returned as separately visible panels (extends stay nested in FillInTheBlanks).
 * Stale answers from unreachable branches are ignored.
 */
export const getVisibleQuestionIds = (
    questions: Question[],
    activity: Activity,
    formValues: QuestionsFormData,
): QuestionId[] => {
    return getQuestionReachability(questions, activity, formValues).visibleQuestionIds
}

/** True when every currently revealed question (visible panels and nested extends) has a non-empty answer. */
export const areVisibleQuestionsAnswered = (
    questions: Question[],
    activity: Activity,
    formValues: QuestionsFormData,
): boolean => {
    const { revealedQuestionIds } = getQuestionReachability(questions, activity, formValues)
    if (revealedQuestionIds.length === 0) {
        return false
    }

    return revealedQuestionIds.every((questionId) => {
        const question = questions.find((candidate) => candidate.id === questionId)
        if (!question) {
            return false
        }

        return getSelectedAnswers(formValues[createFormKeyFromActivityAndQuestion(activity, question)]) !== null
    })
}
